import { useState, useEffect, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import LessonBuilder from "@/components/theory/LessonBuilder";
import { Plus, Pencil, Trash2, BookOpen, ChevronRight, ArrowLeft, Layers, CornerDownRight, HelpCircle, Upload, Loader2, Sparkles, ArrowUp, ArrowDown } from "lucide-react";

const DISCIPLINES = ["drone", "helicopter", "aeroplane"];
const LEVELS = ["RPL", "PPL", "CPL"];
const EMPTY_SEC = { title: "", description: "", disciplines: [], levels: [] };

const TagToggle = ({ options, value = [], onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button type="button" key={o} data-testid={`${testid}-${o}`} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
          className={`px-2.5 py-1 rounded-full text-xs border capitalize transition-all ${on ? "bg-lime-400 border-lime-400 text-slate-900" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}>{o}</button>
      );
    })}
  </div>
);

const Tags = ({ items, cls }) => items?.length ? <span className="inline-flex gap-1">{items.map((t) => <Badge key={t} variant="outline" className={`text-[10px] capitalize ${cls}`}>{t}</Badge>)}</span> : <Badge variant="outline" className="text-[10px] text-slate-400">all</Badge>;

const qCount = (l) => (l.elements || []).reduce((n, e) => n + (e.questions || []).length, 0);

// Flatten the lesson tree into an ordered [{l, depth}] list (recursion in data, not in JSX).
const flattenTree = (lessons) => {
  const out = [];
  const walk = (parentId, depth) => {
    lessons.filter((x) => (x.parent_id || null) === parentId).forEach((l) => {
      out.push({ l, depth });
      walk(l.id, depth + 1);
    });
  };
  walk(null, 0);
  return out;
};

function LessonRow({ l, depth, onOpen, onAddSub, onDelete, onGenerate, genBusy }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" style={{ paddingLeft: 16 + depth * 24 }} data-testid={`theory-lesson-row-${l.id}`}>
      {depth > 0 && <CornerDownRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
      <button onClick={() => onOpen(l.id)} className="min-w-0 flex-1 text-left" data-testid={`open-lesson-${l.id}`}>
        <p className="font-medium text-slate-900 truncate flex items-center gap-1.5">{l.title || "Untitled lesson"}</p>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
          <span>{(l.elements || []).length} elements</span><span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-1"><HelpCircle className="h-3 w-3" /> {qCount(l)}</span>
          <Tags items={l.disciplines} cls="border-sky-200 text-sky-600" />
          <Tags items={l.levels} cls="border-indigo-200 text-indigo-600" />
        </div>
      </button>
      <Button size="sm" variant="ghost" className="h-8 text-xs text-lime-600 hover:text-lime-700 hover:bg-lime-50" onClick={() => onGenerate(l.id)} disabled={genBusy} data-testid={`genq-lesson-${l.id}`} title="Generate quiz questions with AI">
        {genBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI Q's
      </Button>
      <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500" onClick={() => onAddSub(l.id)} data-testid={`add-sublesson-${l.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Sub-lesson</Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" onClick={() => onOpen(l.id)} data-testid={`edit-lesson-${l.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => onDelete(l)} data-testid={`delete-lesson-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

// ── Section detail: tree of lessons ──────────────────────────────────────────
export function SectionDetail({ section, onBack, onEditSection, backLabel = "All theory courses" }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builder, setBuilder] = useState(null); // {lessonId} | {new:true, parentId}
  const [delLesson, setDelLesson] = useState(null);
  const [importing, setImporting] = useState(false);
  const [imp, setImp] = useState(null); // import dialog state
  const [genId, setGenId] = useState(null);

  const openImport = () => setImp({
    file: null, qpt: "3", difficulty: "standard",
    level: (section.levels && section.levels[0]) || "",
    discipline: (section.disciplines && section.disciplines[0]) || "",
  });

  const runImport = async () => {
    if (!imp.file) { toast.error("Choose a .pptx file first"); return; }
    if (!imp.file.name.toLowerCase().endsWith(".pptx")) { toast.error("Please choose a .pptx file"); return; }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", imp.file);
    fd.append("section_id", section.id);
    fd.append("level", imp.level);
    fd.append("discipline", imp.discipline);
    fd.append("questions_per_topic", imp.qpt);
    fd.append("difficulty", imp.difficulty);
    try {
      const r = await api.post("/theory/import-pptx", fd);
      toast.success(`Imported “${r.data.title}” — ${r.data.elements?.length || 0} topics created`);
      setImp(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); } finally { setImporting(false); }
  };

  const load = useCallback(async () => {
    try { const r = await api.get(`/theory/sections/${section.id}/lessons`); setLessons(r.data); }
    catch { toast.error("Failed to load lessons"); } finally { setLoading(false); }
  }, [section.id]);
  useEffect(() => { load(); }, [load]);

  if (builder) return (
    <LessonBuilder lessonId={builder.lessonId} sectionId={section.id} parentId={builder.parentId}
      onBack={() => { setBuilder(null); load(); }} />
  );

  const del = async () => {
    try { await api.delete(`/theory/lessons/${delLesson.id}`); toast.success("Lesson deleted"); setDelLesson(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const genQuestions = async (lessonId) => {
    setGenId(lessonId);
    try {
      const r = await api.post(`/theory/lessons/${lessonId}/generate-questions`, { count: 3 });
      const total = (r.data.elements || []).reduce((n, e) => n + (e.questions || []).length, 0);
      toast.success(`Questions generated — ${total} now on this topic`); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Generation failed"); } finally { setGenId(null); }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="theory-section-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="section-back"><ArrowLeft className="h-4 w-4" /> {backLabel}</button>
      <div className="text-white rounded-2xl p-6 flex items-start justify-between gap-4 flex-wrap" style={{ backgroundColor: section.colour || "#0f172a" }}>
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2"><BookOpen className="h-6 w-6" /> {section.title}</h1>
          {section.description && <p className="text-white/80 text-sm mt-1 max-w-2xl">{section.description}</p>}
          <div className="flex items-center gap-2 mt-3"><Tags items={section.disciplines} cls="border-white/30 text-white/90" /><Tags items={section.levels} cls="border-white/30 text-white/90" /></div>
        </div>
        <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={onEditSection} data-testid="edit-section-btn"><Pencil className="h-4 w-4 mr-1.5" /> Edit course</Button>
      </div>
      {section.reference_pdf && (
        <a href={`${API}/files/${section.reference_pdf}?auth=${encodeURIComponent(localStorage.getItem("session_token") || "")}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 -mt-1" data-testid="section-reference-pdf">
          <BookOpen className="h-4 w-4" /> Open reference PDF
        </a>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Lessons</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openImport} data-testid="import-pptx-btn"><Upload className="h-4 w-4 mr-1.5" /> Import PowerPoint</Button>
          <Button onClick={() => setBuilder({ new: true, parentId: null })} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-lesson-btn"><Plus className="h-4 w-4 mr-1.5" /> New lesson</Button>
        </div>
      </div>
      {loading ? <p className="text-sm text-slate-400 py-6 text-center">Loading…</p> : lessons.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-xl">No lessons yet. Add the first lesson to this course.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
          {flattenTree(lessons).map(({ l, depth }) => <LessonRow key={l.id} l={l} depth={depth} onOpen={(id) => setBuilder({ lessonId: id })} onAddSub={(pid) => setBuilder({ new: true, parentId: pid })} onDelete={setDelLesson} onGenerate={genQuestions} genBusy={genId === l.id} />)}
        </div>
      )}

      {imp && (
        <Dialog open onOpenChange={(o) => !o && !importing && setImp(null)}>
          <DialogContent className="max-w-md" data-testid="import-pptx-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Import PowerPoint</DialogTitle>
              <DialogDescription>Turn a .pptx deck into a study lesson with auto-generated quiz questions. Adjust how the AI builds it below.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">PowerPoint file</Label>
                <Input type="file" accept=".pptx" className="mt-1.5" data-testid="import-file-input"
                  onChange={(e) => setImp((p) => ({ ...p, file: e.target.files?.[0] || null }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Questions / topic</Label>
                  <Select value={imp.qpt} onValueChange={(v) => setImp((p) => ({ ...p, qpt: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="import-qpt"><SelectValue /></SelectTrigger>
                    <SelectContent>{["1", "2", "3", "4", "5"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Difficulty</Label>
                  <Select value={imp.difficulty} onValueChange={(v) => setImp((p) => ({ ...p, difficulty: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="import-difficulty"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easier">Easier (recall)</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="harder">Harder (applied)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Level</Label>
                  <Select value={imp.level || "all"} onValueChange={(v) => setImp((p) => ({ ...p, level: v === "all" ? "" : v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="import-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All levels</SelectItem>
                      {["RPL", "PPL", "CPL", "Endorsement"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Discipline</Label>
                  <Select value={imp.discipline || "all"} onValueChange={(v) => setImp((p) => ({ ...p, discipline: v === "all" ? "" : v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="import-discipline"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {["drone", "helicopter", "aeroplane"].map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-slate-400">The AI extracts your slides, preserves regulatory references, and writes study notes plus quiz questions. Larger decks take a little longer.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setImp(null)} disabled={importing}>Cancel</Button>
              <Button onClick={runImport} disabled={importing || !imp.file} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="import-run-btn">
                {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} {importing ? "Importing…" : "Import"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={!!delLesson} onOpenChange={(o) => !o && setDelLesson(null)}>
        <AlertDialogContent data-testid="delete-lesson-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete lesson?</AlertDialogTitle>
            <AlertDialogDescription>This deletes “{delLesson?.title}” and any sub-lessons under it. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-500" data-testid="confirm-delete-lesson">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────
export default function TheoryCourses({ view = "grid" }) {
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [fDisc, setFDisc] = useState("");
  const [fLevel, setFLevel] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_SEC);
  const [saving, setSaving] = useState(false);
  const [delSec, setDelSec] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (fDisc) params.discipline = fDisc;
      if (fLevel) params.level = fLevel;
      const r = await api.get("/theory/sections", { params });
      setSections((r.data || []).filter((s) => s.group !== "vfrg"));
    } catch { toast.error("Failed to load courses"); }
  }, [fDisc, fLevel]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY_SEC, seq: sections.length }); setDialog(true); };
  const openEdit = (s) => { setForm({ ...s }); setDialog(true); };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    const payload = { title: form.title, description: form.description, disciplines: form.disciplines, levels: form.levels,
      seq: Number(form.seq) || 0, colour: form.colour || "", group: form.group || "", reference_pdf: form.reference_pdf || "" };
    try {
      if (form.id) { const r = await api.put(`/theory/sections/${form.id}`, payload); if (selected?.id === form.id) setSelected(r.data); }
      else await api.post("/theory/sections", payload);
      toast.success("Course saved"); setDialog(false); load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const del = async () => {
    try { await api.delete(`/theory/sections/${delSec.id}`); toast.success("Course deleted"); setDelSec(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const secPayload = (s) => ({
    title: s.title, description: s.description || "", disciplines: s.disciplines || [], levels: s.levels || [],
    colour: s.colour || "", group: s.group || "", reference_pdf: s.reference_pdf || "",
  });
  const [reordering, setReordering] = useState(false);
  const move = async (s, dir) => {
    const arr = [...sections];
    const i = arr.findIndex((x) => x.id === s.id);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSections(arr.map((x, idx) => ({ ...x, seq: idx })));   // optimistic
    setReordering(true);
    try {
      await Promise.all(arr.map((x, idx) => api.put(`/theory/sections/${x.id}`, { ...secPayload(x), seq: idx })));
    } catch { toast.error("Reorder failed"); load(); } finally { setReordering(false); }
  };

  if (selected) return <SectionDetail section={selected} onBack={() => { setSelected(null); load(); }} onEditSection={() => openEdit(selected)} />;

  return (
    <div className="space-y-5" data-testid="theory-courses">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Filter</span>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button onClick={() => setFDisc("")} className={`px-2.5 py-1 text-xs rounded-md ${!fDisc ? "bg-slate-900 text-white" : "text-slate-500"}`} data-testid="theory-filter-disc-all">All craft</button>
            {DISCIPLINES.map((d) => <button key={d} onClick={() => setFDisc(d)} className={`px-2.5 py-1 text-xs rounded-md capitalize ${fDisc === d ? "bg-slate-900 text-white" : "text-slate-500"}`} data-testid={`theory-filter-disc-${d}`}>{d}</button>)}
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button onClick={() => setFLevel("")} className={`px-2.5 py-1 text-xs rounded-md ${!fLevel ? "bg-slate-900 text-white" : "text-slate-500"}`} data-testid="theory-filter-level-all">All levels</button>
            {LEVELS.map((l) => <button key={l} onClick={() => setFLevel(l)} className={`px-2.5 py-1 text-xs rounded-md ${fLevel === l ? "bg-slate-900 text-white" : "text-slate-500"}`} data-testid={`theory-filter-level-${l}`}>{l}</button>)}
          </div>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-section-btn"><Plus className="h-4 w-4 mr-1.5" /> New Course Section</Button>
      </div>

      {sections.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl">
          <Layers className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-3">No theory courses yet. Create a section like “Air Law” to get started.</p>
        </div>
      ) : view === "list" ? (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="theory-sections-list">
          {sections.map((s, i) => (
            <div key={s.id} data-testid={`theory-section-row-${s.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
              <span className="inline-flex h-5 min-w-5 px-1 items-center justify-center rounded-md bg-slate-900 text-white text-[11px] font-bold shrink-0" data-testid={`section-order-${s.id}`}>{i + 1}</span>
              <button onClick={() => setSelected(s)} className="min-w-0 flex-1 text-left" data-testid={`open-section-${s.id}`}>
                <p className="font-medium text-slate-900 truncate">{s.title}</p>
                {s.description && <p className="text-xs text-slate-500 truncate">{s.description}</p>}
              </button>
              <div className="hidden md:flex items-center gap-1.5"><Tags items={s.disciplines} cls="border-sky-200 text-sky-600" /><Tags items={s.levels} cls="border-indigo-200 text-indigo-600" /></div>
              <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500 hidden sm:inline-flex shrink-0">{s.lesson_count} lesson{s.lesson_count === 1 ? "" : "s"}</Badge>
              <div className="flex flex-col shrink-0">
                <Button size="icon" variant="ghost" className="h-5 w-7 text-slate-400 hover:text-slate-900 disabled:opacity-30" disabled={i === 0 || reordering} onClick={() => move(s, -1)} data-testid={`section-up-${s.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-5 w-7 text-slate-400 hover:text-slate-900 disabled:opacity-30" disabled={i === sections.length - 1 || reordering} onClick={() => move(s, 1)} data-testid={`section-down-${s.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900 shrink-0" onClick={() => openEdit(s)} data-testid={`edit-section-${s.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600 shrink-0" onClick={() => setDelSec(s)} data-testid={`delete-section-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="theory-sections-grid">
          {sections.map((s, i) => (
            <div key={s.id} data-testid={`theory-section-card-${s.id}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setSelected(s)} className="min-w-0 text-left flex-1" data-testid={`open-section-${s.id}`}>
                  <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-1.5">
                    <span className="inline-flex h-5 min-w-5 px-1 items-center justify-center rounded-md bg-slate-900 text-white text-[11px] font-bold shrink-0" data-testid={`section-order-${s.id}`}>{i + 1}</span>
                    <span className="truncate">{s.title}</span> <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                  </h3>
                  {s.description && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{s.description}</p>}
                </button>
                <div className="flex items-center gap-0.5 shrink-0">
                  <div className="flex flex-col">
                    <Button size="icon" variant="ghost" className="h-5 w-7 text-slate-400 hover:text-slate-900 disabled:opacity-30" disabled={i === 0 || reordering} onClick={() => move(s, -1)} data-testid={`section-up-${s.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-5 w-7 text-slate-400 hover:text-slate-900 disabled:opacity-30" disabled={i === sections.length - 1 || reordering} onClick={() => move(s, 1)} data-testid={`section-down-${s.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" onClick={() => openEdit(s)} data-testid={`edit-section-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => setDelSec(s)} data-testid={`delete-section-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-3">
                <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">{s.lesson_count} lesson{s.lesson_count === 1 ? "" : "s"}</Badge>
                <Tags items={s.disciplines} cls="border-sky-200 text-sky-600" />
                <Tags items={s.levels} cls="border-indigo-200 text-indigo-600" />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg" data-testid="section-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{form.id ? "Edit Course Section" : "New Course Section"}</DialogTitle>
            <DialogDescription>A reusable theory section (e.g. Air Law) shared across syllabi.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Section title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="e.g. Air Law" data-testid="section-title-input" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Priority order</Label>
              <Input type="number" min="1" value={(Number(form.seq) || 0) + 1} onChange={(e) => setForm({ ...form, seq: Math.max(0, (Number(e.target.value) || 1) - 1) })} className="mt-1.5 w-28" data-testid="section-order-input" />
              <p className="text-[11px] text-slate-400 mt-1">Lower number = studied first (e.g. Air Law = 1). You can also reorder with the ↑/↓ arrows on each course card.</p></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={3} placeholder="What this section is about" data-testid="section-desc-input" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Disciplines <span className="normal-case font-normal text-slate-400">(none = all)</span></Label>
              <div className="mt-2"><TagToggle options={DISCIPLINES} value={form.disciplines} onChange={(v) => setForm({ ...form, disciplines: v })} testid="section-disc" /></div></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Levels <span className="normal-case font-normal text-slate-400">(none = all)</span></Label>
              <div className="mt-2"><TagToggle options={LEVELS} value={form.levels} onChange={(v) => setForm({ ...form, levels: v })} testid="section-level" /></div></div>
            <Button onClick={save} disabled={saving} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="save-section-btn">{saving ? "Saving…" : "Save course"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delSec} onOpenChange={(o) => !o && setDelSec(null)}>
        <AlertDialogContent data-testid="delete-section-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete course section?</AlertDialogTitle>
            <AlertDialogDescription>This deletes “{delSec?.title}” and all its lessons. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-500" data-testid="confirm-delete-section">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
